/**
 * Provider for UserProfile data.
 *
 * Fetches user identity, bidder signature, and wallet via a single
 * GraphQL query. Uses ORIGIN_ONLY resolve policy for fresh data.
 *
 * Returns the raw graph response — transform via buildUserProfile().
 */
import fetch from '@adc/fetch'
import { gql } from '@adc/sdk'
import logger from '@adc/logger'
import type {
  PartyBuyingPreference,
  PartyBuyingPreferenceInput,
} from '@adc/sdk/types'
import type {
  BuyerPreferenceParameters,
  BuyerPreferenceIntents,
} from '../types/buyerPreference'
import type { RawUserProfileResponse } from '../util/buildUserProfile'

export const USER_PROFILE_QUERY = gql`
  query resiFeBtbHome_UserProfileData($userId: ID!) {
    user(userId: $userId) {
      user_id
      party_id
      first_name
      last_name
      email
      status
      is_current_user_remote_bid_qualified

      signature(resolvePolicy: ORIGIN_ONLY) {
        __typename

        ... on OnlineBidderSignature {
          system_derived_persona
          staff_derived_persona
          historical {
            buyer_caused_fallout_count
            auctions_quality_bids_count
            winning_bids_count
            contracts_closed_count
            latest_bid_date
            latest_purchase_date
            latest_registration_date
          }
          current {
            auction_registration_count
            auctions_with_bids_count
            auctions_with_quality_bids_count
            contracts_in_progress_count
            is_first_time_bidder
          }
        }

        ... on LiveBidderSignature {
          historical {
            buyer_caused_fallout_count
            winning_bids_count
            contracts_closed_count
            latest_bid_date
            latest_purchase_date
            latest_registration_date
          }
          current {
            auction_registration_count
            auctions_with_bids_count
            contracts_in_progress_count
          }
        }

        ... on UaaBidderSignature {
          current {
            user_created_date
          }
        }

        ... on ProfileBidderSignature {
          current {
            saved_properties_count
            saved_searches_count
          }
        }
      }

      wallet(refreshWallet: false) {
        aggregations {
          consumable_fund_amount
          payment_type
        }
      }
    }
  }
`

export async function fetchUserProfileData(
  userId: string
): Promise<RawUserProfileResponse> {
  const response = await fetch.forAuctionGraph<'user', RawUserProfileResponse>({
    query: USER_PROFILE_QUERY,
    variables: { userId },
  })

  // Partial data strategy: return whatever we got even if some resolvers errored
  if (response.body.errors?.length) {
    logger.warn(
      '[userInsights] Partial errors in user profile query:',
      response.body.errors
    )
  }

  const user = response.body.data?.user
  if (!user) {
    throw new Error(`User ${userId} not found`)
  }

  return user
}

export const PARTIES_BY_PHONE_QUERY = gql`
  query resiFeBtbHome_PartiesByPhone(
    $filter: PartySearchFilter!
    $resolvePolicy: ResolvePolicy
  ) {
    parties(filter: $filter, resolvePolicy: $resolvePolicy) {
      party_id
      user {
        user_id
      }
    }
  }
`

interface PartyByPhone {
  party_id: string
  user: { user_id: string } | null
}

export async function fetchUserIdByPhone(
  phone: string
): Promise<string | null> {
  const response = await fetch.forAuctionGraph<'parties', PartyByPhone[]>({
    query: PARTIES_BY_PHONE_QUERY,
    variables: { filter: { phone }, resolvePolicy: 'ORIGIN_ONLY' },
  })

  if (response.body.errors?.length) {
    logger.warn(
      '[userInsights] Errors resolving user by phone:',
      response.body.errors
    )
  }

  const parties = response.body.data?.parties ?? []
  const firstWithUser = parties.find(party => party.user?.user_id)
  return firstWithUser?.user?.user_id ?? null
}

// --- Buying Preference ---

export const PARTY_BUYING_PREFERENCE_QUERY = gql`
  query resiFeBtbHome_PartyBuyingPreference($partyId: ID!) {
    party(partyId: $partyId) {
      buying_preference {
        party_buying_preference_id
        party_id
        parameters
        intents
        structured_locations {
          inclusions {
            urn
            label
            location_type
          }
          exclusions {
            urn
            label
            location_type
          }
        }
      }
    }
  }
`

export const SAVE_PARTY_BUYING_PREFERENCE_MUTATION = gql`
  mutation resiFeBtbHome_SavePartyBuyingPreference(
    $partyId: ID!
    $input: PartyBuyingPreferenceInput!
  ) {
    savePartyBuyingPreferenceForParty(partyId: $partyId, input: $input) {
      party_buying_preference_id
      party_id
      parameters
      intents
      structured_locations {
        inclusions {
          urn
          label
          location_type
        }
        exclusions {
          urn
          label
          location_type
        }
      }
    }
  }
`

/**
 * Typed representation of the PartyBuyingPreference with parsed parameters/intents.
 * This extends the SDK type to provide typed access to the JSONObject fields.
 */
export interface TypedBuyingPreference extends PartyBuyingPreference {
  parameters?: BuyerPreferenceParameters | null
  intents?: BuyerPreferenceIntents | null
}

interface PartyBuyingPreferenceResponse {
  party: {
    buying_preference: TypedBuyingPreference | null
  } | null
}

export async function fetchBuyingPreference(
  partyId: string
): Promise<TypedBuyingPreference | null> {
  const response = await fetch.forAuctionGraph<
    'party',
    PartyBuyingPreferenceResponse['party']
  >({
    query: PARTY_BUYING_PREFERENCE_QUERY,
    variables: { partyId },
  })

  if (response.body.errors?.length) {
    logger.warn(
      '[userInsights] Errors fetching buying preference:',
      response.body.errors
    )
  }

  return response.body.data?.party?.buying_preference ?? null
}

export async function saveBuyingPreference(
  partyId: string,
  input: PartyBuyingPreferenceInput
): Promise<TypedBuyingPreference> {
  const response = await fetch.forAuctionGraph<
    'savePartyBuyingPreferenceForParty',
    TypedBuyingPreference
  >({
    query: SAVE_PARTY_BUYING_PREFERENCE_MUTATION,
    variables: { partyId, input },
  })

  if (response.body.errors?.length) {
    throw new Error(
      `Failed to save buying preference: ${response.body.errors[0].message}`
    )
  }

  return response.body.data.savePartyBuyingPreferenceForParty
}
